/**
 * Stavový automat „drž pro pohyb" u PTZ kamery.
 *
 * Proč vlastní automat a ne jen `useState`: obraz z kamery má ~6 s zpoždění (HLS),
 * takže po stisku se dlouho vizuálně nic nestane a uživatel neví, jestli tlačítko
 * vůbec reaguje. Automat drží čtyři informace, které UI potřebuje ukázat *hned*:
 *
 *  1. stisk je registrovaný (fáze `starting` nastane 0 ms po pointerdown, ne až po
 *     odpovědi serveru),
 *  2. jak dlouho se drží a kolik zbývá do bezpečnostního stropu,
 *  3. že se kamera zastavila, ale obraz ještě dojíždí (fáze `settling`),
 *  4. že se stop NEPODAŘIL — to je jediný stav, kdy kamera fyzicky jede dál a
 *     uživatel o tom musí vědět.
 *
 * Modul je záměrně čistý (žádné timery, žádné API) — časovače a requesty řeší
 * `hooks/usePtzHold.ts`, tady je jen logika, kterou lze unit-testovat.
 */

import type { PtzDirection } from "@client/api/foscam";

/** Osa pohybu — 8 směrů pan/tilt + zoom. Zoom má vlastní CGI příkazy, ale stejný „drž" model. */
export type PtzAxis = PtzDirection | "zoomIn" | "zoomOut";

export type HoldPhase =
  /** Nic se neděje. */
  | "idle"
  /** Stisknuto, `ptzMove*` request letí. Prstenec se už plní. */
  | "starting"
  /** Server potvrdil, kamera jede. */
  | "moving"
  /** Puštěno (nebo strop), `ptzStopRun` letí — případně opakovaně. */
  | "stopping"
  /** Kamera stojí, obraz dojíždí latenci streamu. */
  | "settling"
  /** Move selhal, nebo se nepodařil stop ani po všech pokusech. */
  | "failed";

export interface HoldState {
  phase: HoldPhase;
  /** Osa, která se drží (nebo doběhla). `null` jen v `idle`. */
  axis: PtzAxis | null;
  /** `Date.now()` stisku — základ pro `holdProgress`. */
  pressedAt: number;
  /** `Date.now()` puštění, dokud drží `null`. */
  releasedAt: number | null;
  /** Kolik stop requestů už selhalo (0 = zatím žádný pokus neselhal). */
  stopAttempts: number;
  /** Do kdy „obraz dojíždí" (fáze `settling`). */
  settleUntil: number | null;
  /** User-facing hláška pro `failed`. */
  error: string | null;
  /** True když pohyb ukončil bezpečnostní strop, ne uživatel. */
  hitSafetyCap: boolean;
  /** True když se stop nepodařil ani po vyčerpání pokusů — kamera možná pořád jede. */
  stopLost: boolean;
}

/**
 * Bezpečnostní strop držení. Bez něj by zaseknutý `pointerup` (typicky přepnutá
 * aplikace na Androidu, kdy `pointercancel` nemusí dorazit) otáčel kamerou
 * donekonečna — kamera jede do `ptzStopRun`, ne po časový limit.
 */
export const MAX_HOLD_MS = 8_000;

/** Backoff opakování stopu. Stop je kritický, jeden pokus nestačí. */
export const STOP_RETRY_DELAYS_MS = [250, 500, 1_000] as const;

/** Kolikrát celkem se stop zkusí (první pokus + retry). */
export const STOP_MAX_ATTEMPTS = STOP_RETRY_DELAYS_MS.length + 1;

/** Dojezd obrazu se klampuje — měřená latence může být nesmyslná. */
export const SETTLE_MIN_MS = 1_000;
export const SETTLE_MAX_MS = 15_000;

/** Jak dlouho zůstane viditelný chybový stav, než se automat vrátí do `idle`. */
export const FAILED_LINGER_MS = 4_000;

export type HoldEvent =
  | { t: "press"; axis: PtzAxis; now: number }
  | { t: "startOk"; now: number }
  | { t: "startFail"; now: number; message: string }
  | { t: "release"; now: number }
  | { t: "safetyCap"; now: number }
  | { t: "stopOk"; now: number; latencyMs: number }
  | { t: "stopFail"; now: number; message: string }
  | { t: "tick"; now: number }
  | { t: "reset" };

export const initialHoldState: HoldState = {
  phase: "idle",
  axis: null,
  pressedAt: 0,
  releasedAt: null,
  stopAttempts: 0,
  settleUntil: null,
  error: null,
  hitSafetyCap: false,
  stopLost: false,
};

const clamp = (n: number, min: number, max: number): number => Math.min(max, Math.max(min, n));

/** Fáze, ve kterých kamera (podle našeho nejlepšího vědomí) fyzicky jede. */
const isRunning = (p: HoldPhase): boolean => p === "starting" || p === "moving";

/** Fáze, ve kterých nový stisk nesmí přepsat probíhající pohyb. */
const isBusy = (p: HoldPhase): boolean => isRunning(p) || p === "stopping";

export function reduceHold(s: HoldState, e: HoldEvent): HoldState {
  switch (e.t) {
    case "press": {
      // Jedna osa v jednu chvíli. `ptzStopRun` je globální, takže překryv dvou
      // směrů by vedl k tomu, že stop jednoho zastaví pohyb druhého a UI by
      // ukazovalo běžící prstenec u kamery, která stojí.
      if (isBusy(s.phase)) return s;
      return {
        ...initialHoldState,
        phase: "starting",
        axis: e.axis,
        pressedAt: e.now,
      };
    }

    case "startOk":
      // Puštění mohlo přijít dřív než odpověď (rychlý tap) — pak už jsme ve
      // `stopping` a do `moving` se vracet nesmíme.
      return s.phase === "starting" ? { ...s, phase: "moving" } : s;

    case "startFail":
      if (!isRunning(s.phase)) return s;
      return { ...s, phase: "failed", error: e.message, releasedAt: e.now };

    case "release":
      if (!isRunning(s.phase)) return s;
      return { ...s, phase: "stopping", releasedAt: e.now };

    case "safetyCap":
      if (!isRunning(s.phase)) return s;
      return { ...s, phase: "stopping", releasedAt: e.now, hitSafetyCap: true };

    case "stopOk": {
      if (s.phase !== "stopping") return s;
      const settle = clamp(e.latencyMs, SETTLE_MIN_MS, SETTLE_MAX_MS);
      return { ...s, phase: "settling", settleUntil: e.now + settle };
    }

    case "stopFail": {
      if (s.phase !== "stopping") return s;
      const attempts = s.stopAttempts + 1;
      if (attempts >= STOP_MAX_ATTEMPTS) {
        return { ...s, phase: "failed", stopAttempts: attempts, error: e.message, stopLost: true };
      }
      return { ...s, stopAttempts: attempts };
    }

    case "tick": {
      if (s.phase === "settling" && s.settleUntil !== null && e.now >= s.settleUntil) {
        return initialHoldState;
      }
      // Chybu necháme chvíli vidět, pak zmizí sama — kromě „stop se nepodařil",
      // ten musí zůstat, dokud ho uživatel nevyřeší tlačítkem Stop.
      if (s.phase === "failed" && !s.stopLost && s.releasedAt !== null && e.now - s.releasedAt >= FAILED_LINGER_MS) {
        return initialHoldState;
      }
      return s;
    }

    case "reset":
      return initialHoldState;
  }
}

/**
 * 0..1 — kolik z bezpečnostního stropu je vyčerpáno. Řídí plnění prstence
 * během držení. Mimo běžící fáze vrací 0.
 */
export function holdProgress(s: HoldState, now: number): number {
  if (!isRunning(s.phase)) return 0;
  return clamp((now - s.pressedAt) / MAX_HOLD_MS, 0, 1);
}

/**
 * 1..0 — kolik z dojezdu obrazu zbývá. Řídí *odpočet* prstence po puštění,
 * takže uživatel vidí, kdy se obraz dorovná. Mimo `settling` vrací 0.
 */
export function settleProgress(s: HoldState, now: number): number {
  if (s.phase !== "settling" || s.settleUntil === null) return 0;
  const total = s.settleUntil - (s.releasedAt ?? s.settleUntil);
  if (total <= 0) return 0;
  return clamp((s.settleUntil - now) / total, 0, 1);
}

/** Sekundy s jednou desetinou, česká desetinná čárka. */
const secs = (ms: number): string => (Math.max(0, ms) / 1000).toFixed(1).replace(".", ",");

/**
 * Text pod tlačítkem. Vrací `null`, když není co říct — volající pak nic
 * nerenderuje (žádné prázdné místo v layoutu).
 */
export function holdLabel(s: HoldState, now: number): string | null {
  switch (s.phase) {
    case "starting":
    case "moving":
      return `${secs(now - s.pressedAt)} s`;
    case "stopping":
      return s.hitSafetyCap ? `limit ${MAX_HOLD_MS / 1000} s` : "zastavuji…";
    case "settling":
      return s.settleUntil !== null ? `obraz dojíždí ${secs(s.settleUntil - now)} s` : null;
    case "failed":
      return s.stopLost ? "STOP SELHAL" : (s.error ?? "chyba");
    case "idle":
      return null;
  }
}

/** Barevný tón pro prstenec/lištu. Mapuje se na design tokeny v komponentě. */
export type HoldTone = "idle" | "pending" | "active" | "settle" | "danger";

export function holdTone(s: HoldState): HoldTone {
  switch (s.phase) {
    case "starting":
      return "pending";
    case "moving":
    case "stopping":
      return "active";
    case "settling":
      return "settle";
    case "failed":
      return "danger";
    case "idle":
      return "idle";
  }
}

/** Progres pro UI: během držení roste, během dojezdu klesá. */
export function ringProgress(s: HoldState, now: number): number {
  if (s.phase === "settling") return settleProgress(s, now);
  if (s.phase === "stopping") return 1;
  if (s.phase === "failed") return 1;
  return holdProgress(s, now);
}
